import moment from "moment";
import queryString from "query-string";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { xml2js } from "../../json/xml2js";
import {
  cloneForm,
  compareForms,
  deleteFormSubmission,
  getCareTeams,
  getClientData,
  getConfig,
  getFormsySubmissionsForClient,
  getSubmission,
  handleFormChange,
  saveForm,
  sendShareEmail,
  validateForm,
} from "../../scripts/formScripts";
import {
  ArrayCheck,
  getFieldDate,
  getFieldName,
  getFieldValue,
} from "../../scripts/helperScripts";
import BackIcon from "../common/BackIcon";
import ClientFormHeader from "../common/ClientFormHeader";
import Comment from "../common/Comment";
import {
  Input,
  PhoneInput,
  RadioButton,
  Select,
  TextArea,
} from "../common/ControlBundle";
import FormButtonBar from "../common/FormButtonBar";
import ShareModal from "../common/ShareModal";
import Signature from "../common/Signature";
import { getAllergies, getFacilityAddresses } from "../services/soar";
import { getSchema } from "./joi/validation";

function ALTFCCAPRCA() {
  const location = useLocation();
  const navigate = useNavigate();
  const initialProgram = location.state?.selectedProgram;

  //Get route and query string params
  const { clientid, formid, submissionid } = useParams();
  const qs = queryString.parse(location.search);
  console.log("Params", clientid, formid, submissionid);
  console.log("QS:", qs);

  //Get logged in user info
  const safyInfo = JSON.parse(localStorage.getItem("safyInfo"));
  //The complete Form Submission object with form data
  const [formsyDetails, setFormsyDetails] = useState({});
  const [formsyConfig, setFormsyConfig] = useState({});
  const [loadingConfig, setLoadingConfig] = useState(false);
  //The form data and previous forms
  const [data, setData] = useState({});
  const [allFormSubmissions, setAllFormSubmissions] = useState([]);
  //Touched fields
  const [touched, setTouched] = useState({});
  const [isSaveInProgress, setIsSaveInProgress] = useState(false);
  //Errors
  const [errors, setErrors] = useState({});
  //Client, Program and CareTeam Info
  const [clientData, setClientData] = useState({});
  const [careTeams, setCareTeams] = useState([]);

  //Signatures State Property
  const [signatures, setSignatures] = useState([]);
  //Show Signature Modal
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  //Comments state Property
  const [comments, setComments] = useState();
  //Show Comments Modal
  const [showComment, setShowComment] = useState(false);
  //Show Share Modal
  const [showShare, setShowShare] = useState(false);
  //Show Form Level (not field specific) Comments
  const [showFormComment, setShowFormComment] = useState(false);

  const [doCompare, setDoCompare] = useState(false);
  const [timeoutId, setTimeoutId] = useState();

  const pdfRef = useRef(true);
  const isMountedRef = useRef(true);

  //Get validation schema
  const schema = getSchema(formid);
  console.log("Schema Config: ", schema);

  const selectedProgramId = useMemo(() => {
    return data.SelectedProgram?.["@value"];
  }, [data.SelectedProgram]);

  const formDisabled = useMemo(() => {
    return formsyDetails.Active === "0" || formsyDetails.FinalSigned === "1";
  }, [formsyDetails]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      pdfRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (
      !initialProgram?.ccx_client_programid ||
      !data.SelectedProgram ||
      data.SelectedProgram["@value"]
    ) {
      return;
    }

    const obj = { ...data };
    obj.SelectedProgram["@value"] = initialProgram.ccx_client_programid;
    setData(obj);

    if (formsyDetails.ClientProgramId === initialProgram.ccx_client_programid) {
      return;
    }

    const details = { ...formsyDetails };
    details.ClientProgramId = initialProgram.ccx_client_programid;
    details.ClientProgramName = getFieldName(initialProgram.ccx_programunit);
    setFormsyDetails(details);
  }, [initialProgram, selectedProgramId, data, formsyDetails]);

  //Get Client Info
  useEffect(() => {
    if (!formid || !clientid) {
      return;
    }

    const loadClientInfo = async () => {
      const response = await Promise.all([
        getFormsySubmissionsForClient(formid, clientid),
        getCareTeams(clientid),
      ]);

      if (!isMountedRef.current || !response) {
        return;
      }

      setAllFormSubmissions(response[0]);
      setCareTeams(response[1]);
    };

    loadClientInfo();

    if (submissionid !== undefined) {
      const loadSubmission = async () => {
        const response = await getSubmission(submissionid);

        if (!isMountedRef.current || !response) {
          return;
        }

        setData(response.Data);
        setFormsyDetails(response.FormsyDetails);

        if (response.Comments) {
          setComments(response.Comments);
        }

        if (response.Signatures) {
          setSignatures(response.Signatures);
        }
      };

      loadSubmission();
    }
  }, [formid, clientid, submissionid]);

  useEffect(() => {
    if (
      !formid ||
      !clientid ||
      Object.keys(formsyConfig).length > 0 ||
      loadingConfig ||
      !safyInfo?.user
    ) {
      return;
    }

    const loadConfig = async () => {
      setLoadingConfig(true);
      const response = await getConfig(formid);

      if (!isMountedRef.current || !response) {
        return;
      }

      setFormsyConfig(response);
      const clientDataResponse = await getClientData(
        clientid,
        submissionid !== undefined,
        `${response.FormName}${
          response.VersionIntId > 1 ? `v${response.VersionIntId}` : ""
        }`
      );

      if (!isMountedRef.current || !clientDataResponse) {
        return;
      }

      setClientData(clientDataResponse);

      if (!submissionid) {
        //Set Formsy Details
        const newForm = {
          SubmissionId: "",
          FormId: response.FormId,
          FormVersionId: formid,
          ContactId: clientid,
          ContactName: clientDataResponse.client.fullName,
          ClientProgramId: "",
          ClientProgramName: "",
          TertiaryObjectId: "",
          TertiaryObjectName: "",
          FormResults: {
            results: {},
          },

          Comments: "",
          Signatures: "",
          CreatedOn: "",
          CreatedById: safyInfo.user.azureId,
          CreatedByName: safyInfo.user.displayName,
          ModifiedById: safyInfo.user.azureId,
          ModifiedByName: safyInfo.user.displayName,
          FinalSigned: "0",
          FinalSignedDate: "",
          Active: "1",
        };
        setFormsyDetails(newForm);
        setData(response.FormConfiguration[0].config[0]);
        setLoadingConfig(false);

        console.log("New Form:", newForm);
        console.log(
          "loadConfig setData:",
          response.FormConfiguration[0].config[0]
        );
      }
    };

    loadConfig();
  }, [
    loadingConfig,
    formid,
    clientid,
    safyInfo?.user,
    formsyConfig,
    submissionid,
  ]);

  useEffect(() => {
    if (!submissionid || !!formsyDetails.SubmissionId) {
      return;
    }

    const obj = { ...formsyDetails };
    obj.SubmissionId = submissionid;
    setFormsyDetails(obj);
  }, [submissionid, formsyDetails]);

  const getSelectedProgramName = (dataObj, cData) => {
    if (!dataObj.SelectedProgram || !cData.programs) {
      return "";
    }

    let name = getFieldName(dataObj.SelectedProgram);
    if (name) {
      return name;
    } else {
      const selectedProgram = cData.programs.filter(
        (p) => p.ccx_client_programid === getFieldValue(dataObj.SelectedProgram)
      )[0];

      if (selectedProgram) {
        name = getFieldName(selectedProgram.ccx_programunit);
        const programDates = ` (${getFieldDate(
          selectedProgram.ccx_admitdate
        )} - ${
          selectedProgram.ccx_dischargedate
            ? getFieldDate(selectedProgram.ccx_dischargedate)
            : "no date"
        })`;
        return name + programDates;
      } else {
        return "";
      }
    }
  };

  //Form validation
  const validate = () => {
    const results = validateForm(data, schema);
    setErrors(results);
    return results;
  };

  const handleChange = ({ currentTarget: input }) => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    const result = handleFormChange(input, data, schema, errors);
    setData(result.Data);
    setErrors(result.Errors);

    if (!submissionid) {
      return result.Data;
    }

    const touchedObj = { ...touched, [input.name]: true };
    const touchedFields = Object.keys(touchedObj).filter(
      (field) => touchedObj[field]
    );

    if (touchedFields.length >= 5) {
      if (isSaveInProgress) {
        const toId = setTimeout(() => {
          touchedFields.forEach((field) => (touchedObj[field] = false));
          handleSubmit(result.Data, null, null, null, true);
        }, 20000);

        setTimeoutId(toId);
      } else {
        clearTimeout(timeoutId);
        handleSubmit(result.Data, null, null, null, true);
        touchedFields.forEach((field) => (touchedObj[field] = false));
      }
    } else {
      const toId = setTimeout(() => {
        if (isSaveInProgress) {
          const toId2 = setTimeout(() => {
            touchedFields.forEach((field) => (touchedObj[field] = false));
            handleSubmit(result.Data, null, null, null, true);
          }, 20000);

          setTimeoutId(toId2);
        } else {
          touchedFields.forEach((field) => (touchedObj[field] = false));
          handleSubmit(result.Data, null, null, null, true);
        }
      }, 20000);

      setTimeoutId(toId);
    }

    setTouched(touchedObj);
    return result.Data;
  };

  ////SAVE
  const handleSubmit = async (
    dataOverride,
    formsyDetailsOverride,
    commentsOverride,
    signaturesOverride,
    autoSave = false
  ) => {
    if (isSaveInProgress) {
      return;
    }

    const dataObj = dataOverride || data;
    const formsyDetailsObj = formsyDetailsOverride || formsyDetails;
    const commentsObj =
      commentsOverride === undefined ? undefined : commentsOverride || comments;
    const signaturesObj = signaturesOverride || signatures;

    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    setTouched({});
    const errs = { ...errors };

    if (!dataObj.SelectedProgram?.["@value"]) {
      errs.SelectedProgram = '"Selected Program" is required';
      setErrors(errs);
      return;
    }

    delete errs.SelectedProgram;
    setErrors(errs);
    setIsSaveInProgress(true);

    try {
      const submissionResponse = await saveForm(
        dataObj,
        formsyDetailsObj,
        commentsObj,
        signaturesObj,
        navigate,
        location,
        autoSave
      );

      if (!isMountedRef.current || !submissionResponse?.Id) {
        return;
      }

      const newDetails = { ...formsyDetailsObj };
      newDetails.ModifiedOn = submissionResponse.LastModifiedOn;
      newDetails.ModifiedById = safyInfo.user.azureId;
      newDetails.ModifiedByName = safyInfo.user.displayName;

      if (newDetails.SubmissionId === "") {
        newDetails.SubmissionId = submissionResponse?.Id;
      }

      setFormsyDetails(newDetails);
    } finally {
      setIsSaveInProgress(false);
    }
  };

  ////CLONE
  const handleOnClone = (event) => {
    event.preventDefault();
    cloneForm(
      data,
      formsyDetails,
      comments,
      signatures,
      navigate,
      location,
      formsyConfig
    );
    setSignatures([]);
  };

  ////DELETE
  const handleDelete = (e) => {
    e.preventDefault();
    if (submissionid) {
      deleteFormSubmission(
        data,
        formsyDetails,
        comments,
        signatures,
        navigate,
        location
      );
    } else {
      alert("The form could not be deleted because it has not been saved");
    }
  };

  ////SHARE
  const toggleShareModal = () => {
    setShowShare(!showShare);
  };

  const handleShareSend = (recipientList, shareComments) => {
    sendShareEmail(
      recipientList,
      shareComments,
      location.pathname,
      data,
      formsyDetails,
      comments,
      signatures,
      formsyConfig.FormDescription
    );
  };

  ////COMMENTS FUNCTIONALITY
  const [commentToEdit, setCommentToEdit] = useState();

  const toggleCommentModal = (editComment) => {
    if (!showComment && editComment) setCommentToEdit(editComment);
    setShowComment(!showComment);
  };

  const handleComments = ({ action, fieldName, comment, acknowledge }) => {
    if (action === "ADD") {
      setShowComment(true);
    } else if (comment !== undefined) {
      if (action === "EDIT") {
        toggleCommentModal(comment);
      } else if (action === "DELETE") {
        handleDeleteComment(comment);
      } else if (action === "ACKNOWLEDGE" && acknowledge !== undefined) {
        handleAcknowledgeComment(comment, acknowledge);
      }
    } else if (action === "GET") {
      return parsedComments.filter(
        (comment) => comment.field._text === fieldName
      );
    }
  };

  const commentFields = () => {
    let commentOptions = [];
    commentOptions.push({ _id: "EntireForm", name: "Entire Form----" });
    for (const property in data) {
      if (
        data[property]["@type"] !== "section" &&
        data[property]["@type"] !== "label" &&
        data[property]["@type"] !== "User" &&
        data[property]["@type"] !== "hidden"
      ) {
        const option = {
          _id: property,
          name:
            data[property]["@label"] !== undefined &&
            data[property]["@label"] !== ""
              ? data[property]["@label"]
              : property,
        };
        commentOptions.push(option);
      }
    }
    return commentOptions;
  };

  const parsedComments = useMemo(() => {
    if (!comments) {
      return [];
    }

    const commentObj = xml2js(`<comments type="array">${comments}</comments>`, {
      compact: true,
      spaces: 4,
    });

    return ArrayCheck(commentObj.comments.comment);
  }, [comments]);

  const onCommentSave = (comment, commentToEdit) => {
    let formsyComments;
    if (commentToEdit) {
      const commentXml = `<comment><createdby>${
        commentToEdit.createdby._text
      }</createdby><datetime>${commentToEdit.datetime._text}</datetime><field>${
        commentToEdit.field._text
      }</field><comment>${commentToEdit.comment._text}</comment>${
        commentToEdit.acknowledgedby
          ? `<acknowledgedby>${commentToEdit.acknowledgedby._text}</acknowledgedby><acknowledgedon>${commentToEdit.acknowledgedon._text}</acknowledgedon>`
          : ""
      }</comment>`;
      console.log("CurrentCommentsXML: ", comments);
      console.log("CommentToReplace: ", commentXml);
      console.log("CommentToEdit: ", commentToEdit);
      console.log("CommentToEditXML: ", comment);

      formsyComments =
        comments !== undefined
          ? comments.replace(commentXml, comment)
          : comment;
      setComments(formsyComments);
    } else {
      formsyComments = comments !== undefined ? comments + comment : comment;
      setComments(formsyComments);
    }

    setShowComment(false);
    handleSubmit(null, null, formsyComments, null);
    setCommentToEdit(undefined);
  };

  const handleDeleteComment = (comment) => {
    const commentXml = `<comment><createdby>${
      safyInfo.user.displayName
    }</createdby><datetime>${comment.datetime._text}</datetime><field>${
      comment.field._text
    }</field><comment>${comment.comment._text}</comment>${
      comment.acknowledgedby
        ? `<acknowledgedby>${comment.acknowledgedby._text}</acknowledgedby><acknowledgedon>${comment.acknowledgedon._text}</acknowledgedon>`
        : ""
    }</comment>`;

    let formsyComments = comments.replace(commentXml, "");
    if (formsyComments === "") formsyComments = undefined;
    setComments(formsyComments);
    handleSubmit(null, null, formsyComments, null);
  };

  const toggleCommentToEdit = (comment) => {
    setCommentToEdit(comment);
    setShowComment(true);
  };

  const handleAcknowledgeComment = (comment, acknowledge) => {
    if (acknowledge) {
      const oldCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment></comment>`;
      const newCommentXML = `<comment><createdby>${
        comment.createdby._text
      }</createdby><datetime>${comment.datetime._text}</datetime><field>${
        comment.field._text
      }</field><comment>${comment.comment._text}</comment><acknowledgedby>${
        safyInfo.user.displayName
      }</acknowledgedby><acknowledgedon>${moment().format(
        "L"
      )} ${moment().format("LT")}</acknowledgedon></comment>`;
      const formsyComments = comments.replace(oldCommentXML, newCommentXML);
      setComments(formsyComments);
      handleSubmit(null, null, formsyComments, null);
    } else {
      const oldCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment><acknowledgedby>${comment.acknowledgedby._text}</acknowledgedby><acknowledgedon>${comment.acknowledgedon._text}</acknowledgedon></comment>`;
      const newCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment></comment>`;
      const formsyComments = comments.replace(oldCommentXML, newCommentXML);
      setComments(formsyComments);
      handleSubmit(null, null, formsyComments, null);
    }
  };

  const handleCommentNavigation = (fieldName) => {
    const element = document.getElementById(fieldName);
    if (element.type === "checkbox")
      scrollTo(
        document.getElementById(fieldName).parentElement.parentElement
          .parentElement.offsetTop - 50,
        () => {
          document.getElementById(fieldName).focus();
        }
      );
    else
      scrollTo(document.getElementById(fieldName).offsetTop - 50, () => {
        document.getElementById(fieldName).focus();
      });
  };

  const renderComments = () => {
    return parsedComments
      .filter(
        (comment) =>
          comment.field._text === "EntireForm" ||
          comment.field._text === "Entire Form----"
      )
      .map((comment, index) => (
        <div className="mt-2" key={`form-comment-${index}`}>
          {showFormComment && (
            <div
              className={`${
                comment.acknowledgedby ? "bg-safy3-50" : "bg-warning"
              } p-2 m-2 rounded d-print-none`}
              title={
                comment.acknowledgedby
                  ? `Acknowledged by: ${comment.acknowledgedby._text} ${comment.acknowledgedon._text}`
                  : ""
              }
            >
              <span className="h6">
                {comment.createdby._text ===
                  JSON.parse(localStorage.getItem("safyInfo")).user
                    .displayName && (
                  <>
                    <i
                      title="Delete Comment"
                      className="far fa-trash-alt text-white me-2"
                      onClick={() => {
                        confirmAlert({
                          title: "Delete Comment",
                          message:
                            "Are you sure you want to delete this comment?",
                          buttons: [
                            {
                              label: "Yes",
                              onClick: () => {
                                handleDeleteComment(comment);
                              },
                            },
                            {
                              label: "No",
                              onClick: () => {
                                /* do nothing */
                              },
                            },
                          ],
                        });
                      }}
                    ></i>
                    {!comment.acknowledgedby && (
                      <i
                        title="Edit Comment"
                        className="fas fa-pen text-white me-2"
                        onClick={(e) => {
                          toggleCommentToEdit(comment);
                        }}
                      ></i>
                    )}
                  </>
                )}
                {comment.acknowledgedby && (
                  <i
                    className="far fa-check-square text-white me-2"
                    onClick={() => {
                      if (
                        comment.acknowledgedby._text ===
                        JSON.parse(localStorage.getItem("safyInfo")).user
                          .displayName
                      )
                        handleAcknowledgeComment(comment, false);
                    }}
                  ></i>
                )}
                {!comment.acknowledgedby && (
                  <i
                    className="far fa-square text-white me-2"
                    onClick={() => {
                      handleAcknowledgeComment(comment, true);
                    }}
                    title="Acknowledge Comment"
                  ></i>
                )}
                <br />
                {`${comment.createdby._text} - ${comment.datetime._text}`}
              </span>
              <br />
              Regarding:{" "}
              {comment.field._text !== "EntireForm" &&
              comment.field._text !== "Entire Form----" ? (
                <button
                  className="btn btn-link"
                  onClick={() => {
                    setTimeout(() => {
                      handleCommentNavigation(comment.field._text);
                    }, 100);
                  }}
                >
                  {comment.field._text}
                </button>
              ) : (
                <>{comment.field._text}</>
              )}
              <br />
              {comment.comment._text}
            </div>
          )}
        </div>
      ));
  };

  function scrollTo(offset, callback) {
    const fixedOffset = offset.toFixed();
    const onScroll = function () {
      if (window.pageYOffset.toFixed() === fixedOffset) {
        window.removeEventListener("scroll", onScroll);
        callback();
      }
    };

    window.addEventListener("scroll", onScroll);
    onScroll();
    window.scrollTo({
      top: offset,
      behavior: "smooth",
    });
  }

  //SIGNATURE FUNCTIONALITY
  const toggleSignatureModal = (e) => {
    e.preventDefault();
    if (!isSaveInProgress) setShowSignatureModal(true);
  };

  const saveSignature = async (signatureData) => {
    console.log("Signature Data:", signatureData);

    let sigArray = [];
    if (Array.isArray(signatureData)) {
      sigArray = signatureData;
    } else {
      sigArray.push(signatureData);
    }
    setSignatures(sigArray);

    const formsyDetailsObj = { ...formsyDetails };

    if (formsyDetailsObj.FinalSigned !== "1" && checkFinalSigned(sigArray)) {
      console.log("Final Signed");
      formsyDetailsObj.FinalSigned = "1";

      if (
        formsyDetailsObj.FinalSignedDate === "" ||
        formsyDetailsObj.FinalSignedDate === "1900-01-01T00:00:00"
      ) {
        formsyDetailsObj.FinalSignedDate = new Date();
      }

      setFormsyDetails(formsyDetailsObj);
      setComments(undefined);
    }

    handleSubmit(
      null,
      formsyDetailsObj,
      formsyDetailsObj.FinalSigned === "1" ? undefined : null,
      sigArray
    );
  };

  const checkFinalSigned = (sigArray) => {
    const requiredSignatureTypes = JSON.parse(
      formsyConfig.HelperConfiguration[0]
    ).finalSignType.split(",");

    const capturedSignatureTypes = sigArray.map((sig) => sig.signatureType);
    console.log("requiredSignatureTypes: ", requiredSignatureTypes);
    console.log("capturedSignatureTypes: ", capturedSignatureTypes);

    const missingSigTypes = requiredSignatureTypes.filter(
      (type) => !capturedSignatureTypes.includes(type)
    );

    return missingSigTypes.length === 0;
  };

  //COMPARE
  const compare = (field) => {
    if (doCompare)
      return compareForms(field, data, allFormSubmissions, formsyDetails);
  };

  const toggleCompare = () => {
    setDoCompare(!doCompare);
  };

  //CUSTOM
  const [allergies, setAllergies] = useState([]);
  const [loadingAllergies, setLoadingAllergies] = useState(false);
  const [allergiesLoaded, setAllergiesLoaded] = useState(false);
  const [officeOptions, setOfficeOptions] = useState([]);
  const [loadingOffices, setLoadingOffices] = useState(false);
  const [officesLoaded, setOfficesLoaded] = useState(false);

  useEffect(() => {
    if (allergiesLoaded || loadingAllergies) {
      return;
    }

    const loadAllergies = async () => {
      setLoadingAllergies(true);
      const response = await getAllergies(clientid);

      if (!isMountedRef.current) {
        return;
      }

      const alrgs = response
        .filter((allergy) => allergy.ccx_expdate === undefined)
        .map((allergy) => allergy.ccx_allergystr);
      setAllergies(alrgs);
      setAllergiesLoaded(true);
      setLoadingAllergies(false);
    };

    loadAllergies();
  }, [clientid, loadingAllergies, allergiesLoaded]);

  useEffect(() => {
    if (
      officesLoaded ||
      loadingOffices ||
      !safyInfo?.user?.safyInfo?.BusinessUnitIdName
    ) {
      return;
    }

    const loadOffices = async () => {
      setLoadingOffices(true);
      const response = await getFacilityAddresses(
        safyInfo.user.safyInfo.BusinessUnitIdName
      );

      if (!isMountedRef.current) {
        return;
      }
      console.log("GetFacilityAddresses: ", response);
      if (response) {
        const options = response
          .filter((address) => address.ccx_state === "AL")
          .map((address) => ({
            _id: address.ccx_agencylocationaddressid,
            name: `SAFY of ${address.ccx_city}`,
            street: address.ccx_line1,
            city: address.ccx_city,
            state: address.ccx_state,
            postalCode: address.ccx_postalcode,
            telephone: address.ccx_telephone1,
          }));

        setOfficeOptions(options);
        setOfficesLoaded(true);
        setLoadingOffices(false);
      }
    };

    loadOffices();
  }, [safyInfo, loadingOffices, officesLoaded]);

  const handleSelectedOfficeChange = ({ currentTarget: input }) => {
    setData((d) => {
      const obj = { ...d };

      if (!input.value) {
        obj.SAFYOfficeAddress["@value"] = "";
        return obj;
      }

      const officeOption = officeOptions.filter(
        (option) => option._id === input.value
      )[0];
      obj.SAFYOfficeAddress["@value"] = `${officeOption.name}
${officeOption.street}
${officeOption.city}, ${officeOption.state} ${officeOption.postalCode}`;
      obj.SAFYOfficeContactNumber["@value"] = officeOption.telephone || "";
      return obj;
    });
  };

  const yesNoOptions = [
    { _id: "1", name: "Yes" },
    { _id: "0", name: "No" },
  ];

  ////UI
  return (
    <>
      <div className="mb-2">
        <BackIcon />
      </div>
      <div className={!submissionid ? "watermarked" : ""} ref={pdfRef}>
        <ClientFormHeader
          formTitle={formsyConfig.FormDescription}
          clientData={clientData.client}
          formsyDetails={formsyDetails}
        ></ClientFormHeader>
        <FormButtonBar
          config={
            formsyConfig.HelperConfiguration
              ? JSON.parse(formsyConfig.HelperConfiguration[0]).buttonBarConfig
              : {
                  sign: {
                    display: false,
                    initialSignatureType: "supervisor",
                  },
                  clone: {
                    display: false,
                  },
                  delete: {
                    display: false,
                  },
                  share: {
                    display: false,
                  },
                  comment: {
                    display: false,
                  },
                }
          }
          onSave={handleSubmit}
          onClone={handleOnClone}
          onDelete={handleDelete}
          onSignClicked={toggleSignatureModal}
          onComment={toggleCommentModal}
          onCompare={toggleCompare}
          onShare={toggleShareModal}
          signed={signatures.length > 0}
          finalSigned={formsyDetails.FinalSigned === "1"}
          validate={validate}
          pdfRef={pdfRef}
          filename={`${formsyConfig.FormDescription}`}
          newestVersion={
            formsyConfig.VersionIntId === formsyConfig.LatestVersionIntId
          }
        />
        <div className="py-2 border-bottom border-dark">
          <b>Selected Client Program:</b>{" "}
          {getSelectedProgramName(data, clientData)}
        </div>
        {renderComments()}
        {parsedComments.filter(
          (comment) =>
            comment.field._text === "EntireForm" ||
            comment.field._text === "Entire Form----"
        ).length > 0 && (
          <i
            className="fas fa-comment-alt text-warning float-end mb-2 ms-2"
            onClick={() => setShowFormComment(!showFormComment)}
          ></i>
        )}
        <div className="mt-4">
          {data && Object.keys(data).length > 0 && (
            <>
              <p>
                <i>
                  INSTRUCTIONS: This agreement will be completed by the SAFY
                  staff on the day of placement for each new recipient placed in
                  the SAFY Crisis & Assessment Program.
                </i>
              </p>
              <b>
                <u>SAFY</u>
              </b>
              <Select
                name="SAFYOffice"
                label="Office:"
                options={officeOptions}
                onChange={handleSelectedOfficeChange}
                disabled={formDisabled}
              />
              <div className="container">
                <div className="row">
                  <div className="col">
                    <TextArea
                      name="SAFYOfficeAddress"
                      label="Address:"
                      error={errors.SAFYOfficeAddress}
                      value={data.SAFYOfficeAddress["@value"]}
                      onChange={handleChange}
                      identical={compare("SAFYOfficeAddress")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      rows="4"
                    />
                  </div>
                  <div className="col">
                    <PhoneInput
                      name="SAFYOfficeContactNumber"
                      label="Contact Number:"
                      error={errors.SAFYOfficeContactNumber}
                      value={data.SAFYOfficeContactNumber["@value"]}
                      onChange={handleChange}
                      identical={compare("SAFYOfficeContactNumber")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
              </div>
              <b>
                <u>Custodial Agency</u>
              </b>
              <div className="container">
                <div className="row">
                  <div className="col">
                    <Input
                      name="CustodialAgencyName"
                      label="Name:"
                      error={errors.CustodialAgencyName}
                      value={data.CustodialAgencyName["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <TextArea
                      name="CustodialAgencyAddress"
                      label="Address:"
                      error={errors.CustodialAgencyAddress}
                      value={data.CustodialAgencyAddress["@value"]}
                      onChange={handleChange}
                      identical={compare("CustodialAgencyAddress")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <PhoneInput
                      name="CustodialAgencyContactNumber"
                      label="Contact Number:"
                      error={errors.CustodialAgencyContactNumber}
                      value={data.CustodialAgencyContactNumber["@value"]}
                      onChange={handleChange}
                      identical={compare("CustodialAgencyContactNumber")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col">
                    <Input
                      name="CustodialCaseworkerName"
                      label="Caseworker Name:"
                      error={errors.CustodialCaseworkerName}
                      value={data.CustodialCaseworkerName["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <TextArea
                      name="CustodialCaseworkerAddress"
                      label="Caseworker Address:"
                      error={errors.CustodialCaseworkerAddress}
                      value={data.CustodialCaseworkerAddress["@value"]}
                      onChange={handleChange}
                      identical={compare("CustodialCaseworkerAddress")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col">
                    <PhoneInput
                      name="CustodialCaseworkerContactNumber"
                      label="Caseworker Contact Number:"
                      error={errors.CustodialCaseworkerContactNumber}
                      value={data.CustodialCaseworkerContactNumber["@value"]}
                      onChange={handleChange}
                      identical={compare("CustodialCaseworkerContactNumber")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <Input
                      name="CustodialCaseworkerEmail"
                      label="Caseworker Email Address:"
                      error={errors.CustodialCaseworkerEmail}
                      value={data.CustodialCaseworkerEmail["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
              </div>
              <b>
                <u>Licensed/Certified Caregiver</u>
              </b>
              <div className="container">
                <div className="row">
                  <div className="col">
                    <Input
                      name="CaregiverName"
                      label="Name:"
                      error={errors.CaregiverName}
                      value={data.CaregiverName["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <TextArea
                      name="CaregiverAddress"
                      label="Address:"
                      error={errors.CaregiverAddress}
                      value={data.CaregiverAddress["@value"]}
                      onChange={handleChange}
                      identical={compare("CaregiverAddress")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
              </div>
              <b>
                <u>Recipient Information</u>
              </b>
              <TextArea
                name="ReasonForPlacement"
                label="Reason for placement:"
                error={errors.ReasonForPlacement}
                value={data.ReasonForPlacement["@value"]}
                onChange={handleChange}
                identical={compare("ReasonForPlacement")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <TextArea
                name="PermanencyPlan"
                label="Permanency plan and anticipated time frame:"
                error={errors.PermanencyPlan}
                value={data.PermanencyPlan["@value"]}
                onChange={handleChange}
                identical={compare("PermanencyPlan")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <TextArea
                name="SpecialNeeds"
                label="Special needs of recipient (including plans for respite care):"
                error={errors.SpecialNeeds}
                value={data.SpecialNeeds["@value"]}
                onChange={handleChange}
                identical={compare("SpecialNeeds")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <TextArea
                name="History"
                label="History and background information (strengths, development of recipient, psychological/psychiatric evaluation, attachment/bonding to caregivers and siblings, information on any violent acts committed by recipient, if adjudicated as delinquent describe events and disposition):"
                error={errors.History}
                value={data.History["@value"]}
                onChange={handleChange}
                identical={compare("History")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <TextArea
                name="Procedures"
                label="Procedures for high risk behaviors (i.e., suicidal, homicidal or other high risk behaviors in the home and in the community):"
                error={errors.Procedures}
                value={data.Procedures["@value"]}
                onChange={handleChange}
                identical={compare("Procedures")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <b>
                <u>Restrictive Behavior Management Interventions</u>
              </b>
              <TextArea
                name="PotentialHarm"
                label="Describe the potential risk of harm to self or others:"
                error={errors.PotentialHarm}
                value={data.PotentialHarm["@value"]}
                onChange={handleChange}
                identical={compare("PotentialHarm")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <TextArea
                name="OutOfControlAntecedents"
                label="Describe antecedents to out-of-control behavior:"
                error={errors.OutOfControlAntecedents}
                value={data.OutOfControlAntecedents["@value"]}
                onChange={handleChange}
                identical={compare("OutOfControlAntecedents")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <TextArea
                name="PreviousInterventions"
                label="Describe previous interventions used and their effectiveness:"
                error={errors.PreviousInterventions}
                value={data.PreviousInterventions["@value"]}
                onChange={handleChange}
                identical={compare("PreviousInterventions")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <TextArea
                name="PsychologicalFactors"
                label="Describe psychological and social factors (such as psychosis, history of abuse, or claustrophobia) that influence use of such interventions:"
                error={errors.PsychologicalFactors}
                value={data.PsychologicalFactors["@value"]}
                onChange={handleChange}
                identical={compare("PsychologicalFactors")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <TextArea
                name="MedicalFactors"
                label="Describe medical factors that might put the person at risk:"
                error={errors.MedicalFactors}
                value={data.MedicalFactors["@value"]}
                onChange={handleChange}
                identical={compare("MedicalFactors")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <TextArea
                name="BehaviorManagementInterventions"
                label="Describe specific behavior management interventions that may or may not be used for this recipient:"
                error={errors.BehaviorManagementInterventions}
                value={data.BehaviorManagementInterventions["@value"]}
                onChange={handleChange}
                identical={compare("BehaviorManagementInterventions")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <b>
                <u>Health Status</u>
              </b>
              <div className="container">
                <div className="row">
                  <div className="col">
                    <Input
                      name="Height"
                      label="Height:"
                      error={errors.Height}
                      value={data.Height["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <Input
                      name="Weight"
                      label="Weight:"
                      error={errors.Weight}
                      value={data.Weight["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col">
                    <Input
                      name="HairColor"
                      label="Hair color:"
                      error={errors.HairColor}
                      value={data.HairColor["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <Input
                      name="EyeColor"
                      label="Eye color:"
                      error={errors.EyeColor}
                      value={data.EyeColor["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
              </div>
              <p>
                Allergies (drug, food, environmental):{" "}
                <u>{allergies.length > 0 ? allergies.join(", ") : "none"}</u>
              </p>
              {allergies.length > 0 && (
                <>
                  <ul>
                    <li>
                      <Input
                        name="Reactions"
                        label="Note any reactions:"
                        error={errors.Reactions}
                        value={data.Reactions["@value"]}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </li>
                  </ul>
                  <RadioButton
                    name="EpiPenRequired"
                    label="Is an epi-pen required?"
                    options={yesNoOptions}
                    error={errors.EpiPenRequired}
                    value={data.EpiPenRequired["@value"]}
                    onChange={handleChange}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </>
              )}
              <b>
                <u>Recent Hospitalizations/Surgeries</u>
              </b>
              <TextArea
                name="RecentSurgeries"
                label="List hospital stays or surgeries (particularly within the last year):"
                error={errors.RecentSurgeries}
                value={data.RecentSurgeries["@value"]}
                onChange={handleChange}
                identical={compare("RecentSurgeries")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <RadioButton
                name="SurgeryComplications"
                label="Were there any complications with the surgery; are they still recovering?"
                options={yesNoOptions}
                error={errors.SurgeryComplications}
                value={data.SurgeryComplications["@value"]}
                onChange={handleChange}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              {data.SurgeryComplications["@value"] === "1" && (
                <TextArea
                  name="ComplicationsDetails"
                  label="If yes, please explain:"
                  error={errors.ComplicationsDetails}
                  value={data.ComplicationsDetails["@value"]}
                  onChange={handleChange}
                  identical={compare("ComplicationsDetails")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
              )}
              <RadioButton
                name="Treated"
                label="Have you been treated for any medical condition within the past three months?"
                options={yesNoOptions}
                error={errors.Treated}
                value={data.Treated["@value"]}
                onChange={handleChange}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              {data.Treated["@value"] === "1" && (
                <TextArea
                  name="TreatedDetails"
                  label="If yes, please describe:"
                  error={errors.TreatedDetails}
                  value={data.TreatedDetails["@value"]}
                  onChange={handleChange}
                  identical={compare("TreatedDetails")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
              )}
              <Input
                name="LastDoctorVisit"
                label="When was the last time you saw a doctor?"
                type="date"
                error={errors.LastDoctorVisit}
                value={data.LastDoctorVisit["@value"]}
                onChange={handleChange}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <TextArea
                name="ReasonForDoctorVisit"
                label="What was the reason for this doctor visit?"
                error={errors.ReasonForDoctorVisit}
                value={data.ReasonForDoctorVisit["@value"]}
                onChange={handleChange}
                identical={compare("ReasonForDoctorVisit")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <div className="border p-2">
                <b>
                  <u>AUTHORIZATIONS</u>
                </b>
                <RadioButton
                  name="SafyContactFamily"
                  label="As part of SAFY's standard of care we make attempts to involve primary families in their recipient's treatment while they are in care. This may include monthly communication in regards to family therapy, Treatment planning or updating parent on youth’s progress. In order to do this, we need permission from you. May SAFY have contact with the primary family?"
                  options={yesNoOptions}
                  error={errors.SafyContactFamily}
                  value={data.SafyContactFamily["@value"]}
                  onChange={handleChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                {data.SafyContactFamily["@value"] === "0" && (
                  <TextArea
                    name="SafyNoContactDetails"
                    label="If no, provide the reason why we are not allowed to have contact with the primary family:"
                    error={errors.SafyNoContactDetails}
                    value={data.SafyNoContactDetails["@value"]}
                    onChange={handleChange}
                    identical={compare("SafyNoContactDetails")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                )}
                {data.SafyContactFamily["@value"] === "1" && (
                  <>
                    <Input
                      name="SafyContactRestrictions"
                      label="Are there restrictions?"
                      error={errors.SafyContactRestrictions}
                      value={data.SafyContactRestrictions["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    <div>If yes, please provide us with the following:</div>
                    <div className="container">
                      <div className="row">
                        <div className="col">
                          <Input
                            name="FamilyContactName"
                            label="Name:"
                            error={errors.FamilyContactName}
                            defaultValue={
                              data.FamilyContactName &&
                              data.FamilyContactName["@value"]
                            }
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col">
                          <PhoneInput
                            name="FamilyContactPhoneNumber"
                            label="Phone Number:"
                            error={errors.FamilyContactPhoneNumber}
                            value={getFieldValue(data.FamilyContactPhoneNumber)}
                            onChange={handleChange}
                            identical={compare("FamilyContactPhoneNumber")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row">
                        <div className="col">
                          <TextArea
                            name="FamilyContactAddress"
                            label="Address:"
                            error={errors.FamilyContactAddress}
                            defaultValue={
                              data.FamilyContactAddress &&
                              data.FamilyContactAddress["@value"]
                            }
                            onChange={handleChange}
                            identical={compare("FamilyContactAddress")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col"></div>
                      </div>
                    </div>
                  </>
                )}
                <RadioButton
                  name="InitialEntry"
                  label="Is this the recipient's initial entry into care?"
                  options={yesNoOptions}
                  error={errors.InitialEntry}
                  value={data.InitialEntry["@value"]}
                  onChange={handleChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                {data.InitialEntry["@value"] === "1" && (
                  <>
                    <RadioButton
                      name="HealthScreening"
                      label="If yes, has the recipient received a 72 hour health screening?"
                      options={yesNoOptions}
                      error={errors.HealthScreening}
                      value={data.HealthScreening["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    {data.HealthScreening["@value"] === "1" && (
                      <>
                        <Input
                          name="HealthScreeningDate"
                          label="Date of Screening:"
                          type="date"
                          error={errors.HealthScreeningDate}
                          value={data.HealthScreeningDate["@value"]}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                        <div>
                          If yes, please ensure SAFY receives a copy of the
                          screening for the file.
                        </div>
                      </>
                    )}
                  </>
                )}
                <RadioButton
                  name="HealthConcerns"
                  label="Are there any current health concerns (physical/mental/behavioral) that need immediate attention?"
                  options={yesNoOptions}
                  error={errors.HealthConcerns}
                  value={data.HealthConcerns["@value"]}
                  onChange={handleChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                {data.HealthConcerns["@value"] === "1" && (
                  <TextArea
                    name="HealthConcernsDetails"
                    label="If yes, identify:"
                    error={errors.HealthConcernsDetails}
                    value={data.HealthConcernsDetails["@value"]}
                    onChange={handleChange}
                    identical={compare("HealthConcernsDetails")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                )}
                <RadioButton
                  name="MentalHealthServices"
                  label="Is the recipient receiving mental health services currently?"
                  options={yesNoOptions}
                  error={errors.MentalHealthServices}
                  value={data.MentalHealthServices["@value"]}
                  onChange={handleChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                {data.MentalHealthServices["@value"] === "1" && (
                  <Input
                    name="MentalHealthServicesDetails"
                    label="If yes, please specify:"
                    error={errors.MentalHealthServicesDetails}
                    value={data.MentalHealthServicesDetails["@value"]}
                    onChange={handleChange}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                )}
                <RadioButton
                  name="RecipientSmokes"
                  label="Does the recipient smoke?"
                  options={yesNoOptions}
                  error={errors.RecipientSmokes}
                  value={data.RecipientSmokes["@value"]}
                  onChange={handleChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <RadioButton
                  name="PermissionToSmoke"
                  label="Does recipient have custodial agency’s permission to smoke?"
                  options={yesNoOptions}
                  error={errors.PermissionToSmoke}
                  value={data.PermissionToSmoke["@value"]}
                  onChange={handleChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <RadioButton
                  name="Unsupervised"
                  label="May recipient be left unsupervised?"
                  options={yesNoOptions}
                  error={errors.Unsupervised}
                  value={data.Unsupervised["@value"]}
                  onChange={handleChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                {data.Unsupervised["@value"] === "1" && (
                  <TextArea
                    name="UnsupervisedDetails"
                    label="If yes, describe amount of time or situations where the recipient may be left unsupervised:"
                    error={errors.UnsupervisedDetails}
                    value={data.UnsupervisedDetails["@value"]}
                    onChange={handleChange}
                    identical={compare("UnsupervisedDetails")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                )}
                <div>
                  If applicable, supply Recipient's GAL contact information:
                </div>
                <div className="container">
                  <div className="row">
                    <div className="col">
                      <Input
                        name="GALCASAWorkerName"
                        label="Name:"
                        error={errors.GALCASAWorkerName}
                        value={data.GALCASAWorkerName["@value"]}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      <PhoneInput
                        name="GALCASAWorkerPhoneNumber"
                        label="Phone Number:"
                        error={errors.GALCASAWorkerPhoneNumber}
                        value={data.GALCASAWorkerPhoneNumber["@value"]}
                        onChange={handleChange}
                        identical={compare("GALCASAWorkerPhoneNumber")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col">
                      <Input
                        name="GALCASAWorkerEmail"
                        label="Email:"
                        error={errors.GALCASAWorkerEmail}
                        value={data.GALCASAWorkerEmail["@value"]}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col"></div>
                  </div>
                </div>
                <div>
                  If applicable, supply Recipient's CASA worker contact
                  information:
                </div>
                <div className="container">
                  <div className="row">
                    <div className="col">
                      <Input
                        name="CASAWorkerName"
                        label="Name:"
                        error={errors.CASAWorkerName}
                        defaultValue={
                          data.CASAWorkerName && data.CASAWorkerName["@value"]
                        }
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      <PhoneInput
                        name="CASAWorkerPhoneNumber"
                        label="Phone Number:"
                        error={errors.CASAWorkerPhoneNumber}
                        value={getFieldValue(data.CASAWorkerPhoneNumber)}
                        onChange={handleChange}
                        identical={compare("CASAWorkerPhoneNumber")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col">
                      <Input
                        name="CASAWorkerEmail"
                        label="Email:"
                        error={errors.CASAWorkerEmail}
                        defaultValue={
                          data.CASAWorkerEmail && data.CASAWorkerEmail["@value"]
                        }
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col"></div>
                  </div>
                </div>
                <RadioButton
                  name="DriversLicense"
                  label="Can recipient obtain driver’s permit or license?"
                  options={yesNoOptions}
                  error={errors.DriversLicense}
                  value={data.DriversLicense["@value"]}
                  onChange={handleChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <RadioButton
                  name="PermissionToDrive"
                  label="Does recipient have permission to drive?"
                  options={yesNoOptions}
                  error={errors.PermissionToDrive}
                  value={data.PermissionToDrive["@value"]}
                  onChange={handleChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <RadioButton
                  name="StayWithFriend"
                  label="Does recipient have permission to spend the night with a friend?"
                  options={yesNoOptions}
                  error={errors.StayWithFriend}
                  value={data.StayWithFriend["@value"]}
                  onChange={handleChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <RadioButton
                  name="RideWithUnderEighteen"
                  label="Is recipient allowed to ride with licensed drivers under the age of 18?"
                  options={yesNoOptions}
                  error={errors.RideWithUnderEighteen}
                  value={data.RideWithUnderEighteen["@value"]}
                  onChange={handleChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
              </div>
              <b>
                <u>Education Information</u>
              </b>
              <TextArea
                name="EducationProviders"
                label="Names and addresses of the most current education providers/school:"
                error={errors.EducationProviders}
                value={data.EducationProviders["@value"]}
                onChange={handleChange}
                identical={compare("EducationProviders")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <Input
                name="LastGrade"
                label="Last grade of school and type of program attended:"
                error={errors.LastGrade}
                value={data.LastGrade["@value"]}
                onChange={handleChange}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <b>
                <u>Transportation &amp; Visitation</u>
              </b>
              <TextArea
                name="VisitationPlan"
                label="Visitation Plan (including with whom the recipient will be visiting & DHR county worker visits to Foster Home):"
                error={errors.VisitationPlan}
                value={data.VisitationPlan["@value"]}
                onChange={handleChange}
                identical={compare("VisitationPlan")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <Input
                name="VisitationFrequency"
                label="Frequency of visits:"
                error={errors.VisitationFrequency}
                value={data.VisitationFrequency["@value"]}
                onChange={handleChange}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <Input
                name="VisitationLocation"
                label="Location of visits:"
                error={errors.VisitationLocation}
                value={data.VisitationLocation["@value"]}
                onChange={handleChange}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <Input
                name="VisitationDuration"
                label="Duration of visits:"
                error={errors.VisitationDuration}
                value={data.VisitationDuration["@value"]}
                onChange={handleChange}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <Input
                name="VisitationLimitations"
                label="Limitations on visits:"
                error={errors.VisitationLimitations}
                value={data.VisitationLimitations["@value"]}
                onChange={handleChange}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <Input
                name="VisitationCourtOrders"
                label="Court orders regulating visits:"
                error={errors.VisitationCourtOrders}
                value={data.VisitationCourtOrders["@value"]}
                onChange={handleChange}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <TextArea
                name="VisitationTransportation"
                label="Transportation arrangements (include days, time, location and responsible person for activities such as visits or therapy):"
                error={errors.VisitationTransportation}
                value={data.VisitationTransportation["@value"]}
                onChange={handleChange}
                identical={compare("VisitationTransportation")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <strong>
                <p>
                  I agree that this recipient will be permitted to practice the
                  chosen religious faith of this recipient or his/her primary
                  family.
                </p>
                <p>
                  I fully understand that the methods of any discipline will be
                  in accordance with the SAFY Discipline and Restraint Policy
                  and the state regulations.
                </p>
                <p>
                  SAFY has provided an explanation and/or copies of the
                  following material to this recipient and foster parents on
                  this date:
                </p>
              </strong>
              <Input
                name="EvacuationPlan"
                label="Evacuation Plan Reviewed and Fire Drill Conducted"
                type="date"
                error={errors.EvacuationPlan}
                value={data.EvacuationPlan["@value"]}
                onChange={handleChange}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <Input
                name="SpecificRules"
                label="Home/Site Specific Rules"
                type="date"
                error={errors.SpecificRules}
                value={data.SpecificRules["@value"]}
                onChange={handleChange}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <Input
                name="PrivacyPractices"
                label="SAFY Notice of Privacy Practices (copy provided to guardian and reviewed with recipients aged 12 and over)"
                type="date"
                error={errors.PrivacyPractices}
                value={data.PrivacyPractices["@value"]}
                onChange={handleChange}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <Input
                name="SexEd"
                label="Age Appropriate HIV/AIDS and Pregnancy Prevention Educational Material"
                type="date"
                error={errors.SexEd}
                value={data.SexEd["@value"]}
                onChange={handleChange}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <Input
                name="ClientRights"
                label="SAFY worker reviewed Recipient Rights &amp; Grievance Procedure"
                type="date"
                error={errors.ClientRights}
                value={data.ClientRights["@value"]}
                onChange={handleChange}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <Input
                name="TeenMomSIDS"
                label="If Teen Mom is being placed, review Sudden Infant Death Syndrome (SIDS) and information that is given to both recipient and foster parents"
                type="date"
                error={errors.TeenMomSIDS}
                value={data.TeenMomSIDS["@value"]}
                onChange={handleChange}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <div>
                View this site:{" "}
                <a href="http://www.babycenter.com/baby-sleep-safety">
                  http://www.babycenter.com/baby-sleep-safety
                </a>{" "}
                to print off information
              </div>
            </>
          )}
          <div>
            <div
              className="btn btn-primary m-2 d-print-none"
              onClick={() => {
                handleSubmit(null, null, null, null);
              }}
            >
              Save
            </div>
            <Signature
              canvasWidth="450"
              canvasHeight="200"
              modalTitle="Report Signatures"
              careTeams={careTeams}
              signatures={signatures}
              onSign={saveSignature}
              clientData={clientData.client}
              showModal={showSignatureModal}
              onHide={() => {
                setShowSignatureModal(false);
              }}
              finalSignType={
                formsyConfig.HelperConfiguration &&
                JSON.parse(formsyConfig.HelperConfiguration[0]).finalSignType
              }
              initialSignatureType={
                formsyConfig.HelperConfiguration &&
                JSON.parse(formsyConfig.HelperConfiguration[0]).signatureType
              }
            ></Signature>
            <Comment
              showModal={showComment}
              commentToEdit={commentToEdit}
              onHide={() => {
                setShowComment(false);
                setCommentToEdit(undefined);
              }}
              onSave={onCommentSave}
              fields={commentFields()}
            ></Comment>
            {showShare ? (
              <ShareModal
                showShare={showShare}
                onHide={() => setShowShare(false)}
                onSend={handleShareSend}
                submissionId={submissionid}
              ></ShareModal>
            ) : null}
          </div>
        </div>
      </div>
    </>
  );
}

export default ALTFCCAPRCA;
